import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SuperAdminLayout } from './SuperAdminLayout';
import {
  BarChart3, TrendingUp, TrendingDown, DollarSign, Users, PieChart
} from 'lucide-react';

const SuperAdminStats = () => {
  const { api } = useAuth();
  const { t, formatCurrency } = useLanguage();
  const [revenueData, setRevenueData] = useState([]);
  const [planDist, setPlanDist] = useState([]);
  const [churnData, setChurnData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [rev, plans, churn] = await Promise.all([
          api.get('/super-admin/stats/revenue?months=12'),
          api.get('/super-admin/stats/plans'),
          api.get('/super-admin/stats/churn?months=6'),
        ]);
        setRevenueData(rev.data.revenue_data || []);
        setPlanDist(plans.data.distribution || []);
        setChurnData(churn.data.churn_data || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [api]);

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  const maxMRR = Math.max(...revenueData.map(d => d.mrr), 1);
  const totalPlanCount = planDist.reduce((a, b) => a + b.count, 0) || 1;
  const PLAN_COLORS_BAR = { start: '#3b82f6', pro: '#fedd00', super: '#00d946' };

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="super-admin-stats">
        <div>
          <h1 className="text-xl font-bold text-white">{t('saStats.title')}</h1>
          <p className="text-sm text-gray-500">{t('saStats.subtitle')}</p>
        </div>

        {/* Revenue Chart */}
        <div className="bg-[#111827] border border-white/5 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign className="w-5 h-5 text-emerald-400" />
            <h2 className="text-sm font-semibold text-white">{t('saStats.mrrTitle')}</h2>
          </div>
          <div className="flex items-end gap-1 h-40">
            {revenueData.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[9px] text-gray-500">{d.mrr > 0 ? `${d.mrr.toFixed(0)}` : ''}</span>
                <div
                  className="w-full bg-gradient-to-t from-[#009b3a] to-[#00d946] rounded-t-sm transition-all"
                  style={{ height: `${Math.max((d.mrr / maxMRR) * 100, 2)}%`, minHeight: '2px' }}
                />
                <span className="text-[8px] text-gray-600 -rotate-45 origin-center whitespace-nowrap">
                  {d.month?.slice(5)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          {/* Plan Distribution */}
          <div className="bg-[#111827] border border-white/5 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <PieChart className="w-5 h-5 text-[#fedd00]" />
              <h2 className="text-sm font-semibold text-white">{t('saStats.planDistTitle')}</h2>
            </div>
            {planDist.length === 0 ? (
              <p className="text-xs text-gray-600 py-4">{t('saStats.emptyData')}</p>
            ) : (
              <div className="space-y-3">
                {planDist.map(p => {
                  const pct = (p.count / totalPlanCount) * 100;
                  const color = PLAN_COLORS_BAR[p.plan] || '#666';
                  return (
                    <div key={p.plan}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold uppercase" style={{ color }}>{p.plan}</span>
                        <span className="text-xs text-gray-400">{p.count} ({pct.toFixed(0)}%) - {formatCurrency(p.revenue, 'EUR', { maximumFractionDigits: 0 })}</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Churn */}
          <div className="bg-[#111827] border border-white/5 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingDown className="w-5 h-5 text-red-400" />
              <h2 className="text-sm font-semibold text-white">{t('saStats.churnTitle')}</h2>
            </div>
            {churnData.length === 0 ? (
              <p className="text-xs text-gray-600 py-4">{t('saStats.emptyData')}</p>
            ) : (
              <div className="space-y-3">
                {churnData.map(d => (
                  <div key={d.month} className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{d.month}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">{t('saStats.cancelledCount', { count: d.cancelled })}</span>
                      <span className={`text-xs font-bold ${d.churn_rate > 5 ? 'text-red-400' : d.churn_rate > 2 ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {d.churn_rate}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* New Companies per Month */}
        <div className="bg-[#111827] border border-white/5 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-[#009b3a]" />
            <h2 className="text-sm font-semibold text-white">{t('saStats.growthTitle')}</h2>
          </div>
          <div className="flex items-end gap-2 h-32">
            {revenueData.map((d, i) => {
              const maxNew = Math.max(...revenueData.map(r => r.new_companies), 1);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[9px] text-gray-500">{d.new_companies > 0 ? d.new_companies : ''}</span>
                  <div
                    className="w-full bg-gradient-to-t from-[#fedd00]/80 to-[#fedd00] rounded-t-sm"
                    style={{ height: `${Math.max((d.new_companies / maxNew) * 100, 2)}%`, minHeight: '2px' }}
                  />
                  <span className="text-[8px] text-gray-600">{d.month?.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminStats;
